import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PropTypes from "prop-types";
import Category90DayAverage from "./Category90DayAverage";
import { useCategoryHistoricalAverage } from "../hooks/useApi";
import { PERSON_COLORS } from "../utils/people";
import { buildPersonSpending } from "../utils/personSpending";

const COLORS = [
  "#8884d8",
  "#83a6ed",
  "#8dd1e1",
  "#82ca9d",
  "#a4de6c",
  "#d0ed57",
  "#ffc658",
  "#ff8042",
  "#ff6361",
  "#bc5090",
  "#58508d",
  "#003f5c",
  "#444e86",
  "#955196",
  "#dd5182",
  "#ff6e54",
  "#ffa600",
];

// Custom label for pie chart to prevent overlapping
const renderCustomizedLabel = (props) => {
  const { cx, cy, midAngle, outerRadius, percent, index, name } = props;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Only render label if the segment is significant enough
  if (percent < 0.02) return null;

  return (
    <text
      x={x}
      y={y}
      fill={COLORS[index % COLORS.length]}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize="12"
      fontWeight="500"
    >
      {`${name} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
};

const CategoryTab = ({
  spendByCategory,
  transactions,
  formatCurrency,
  budgetComparison = [],
}) => {
  // Fetch historical category averages (6 months by default for better seasonal representation)
  const { data: categoryHistoricalData = [], isPending: averagesPending } = useCategoryHistoricalAverage({ 
    topN: 10, 
    days: 180 // 6 months gives better seasonal patterns than 90 days
  });

  // Memoize chart data to prevent unnecessary re-renders
  const chartData = useMemo(() => {
    if (!Array.isArray(spendByCategory) || spendByCategory.length === 0) {
      return [];
    }
    return spendByCategory;
  }, [spendByCategory]);

  // Calculate sub-category spending (only for transactions with sub-categories)
  const calculateSubCategorySpend = () => {
    const transactionsWithSubCategories = transactions.filter(
      (t) => t.sub_category,
    );

    const subCategorySpend = transactionsWithSubCategories.reduce(
      (acc, transaction) => {
        const subCategory = transaction.sub_category;
        if (!acc[subCategory]) {
          acc[subCategory] = 0;
        }
        acc[subCategory] += transaction.amount;
        return acc;
      },
      {},
    );

    return Object.entries(subCategorySpend)
      .map(([name, value]) => ({
        name,
        value: parseFloat(value.toFixed(2)),
      }))
      .sort((a, b) => b.value - a.value);
  };

  const personSpending = useMemo(
    () => buildPersonSpending(transactions || [], budgetComparison),
    [transactions, budgetComparison],
  );
  const personData = personSpending.people;

  const renderPersonLabel = (props) => {
    const { cx, cy, midAngle, outerRadius, percent, name } = props;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.02) return null;

    return (
      <text
        x={x}
        y={y}
        fill={PERSON_COLORS[name] || "#6b7280"}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="500"
      >
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };

  const subCategoryData = calculateSubCategorySpend();
  const totalSubCategorySpend = subCategoryData.reduce(
    (sum, item) => sum + item.value,
    0,
  );
  const totalSpent = chartData.reduce((sum, cat) => sum + (cat.value || 0), 0);
  const subCategoryPercentageOfTotal =
    totalSpent > 0 ? (totalSubCategorySpend / totalSpent) * 100 : 0;

  // Don't render charts if we have no data at all (only on initial load)
  if (!Array.isArray(spendByCategory)) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading category data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Original charts section */}
      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-1/2 h-80 mb-6 lg:mb-0">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Spending Breakdown
          </h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" key={`pie-${chartData.length}`}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  labelLine={true}
                  label={renderCustomizedLabel}
                  isAnimationActive={true}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}-${entry.name}-${entry.value}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No spending data available for the selected period
            </div>
          )}
        </div>
        <div className="w-full lg:w-1/2">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Top Categories
          </h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400} key={`bar-${chartData.length}`}>
              <BarChart
                data={chartData.slice(0, 10)} // Show top 10 categories
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <YAxis type="category" dataKey="name" width={100} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#8884d8" isAnimationActive={true}>
                  {chartData.slice(0, 10).map((entry, index) => (
                    <Cell
                      key={`cell-${index}-${entry.name}-${entry.value}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-96 text-gray-500">
              No spending data available for the selected period
            </div>
          )}
        </div>
      </div>

      {/* Sub-category analysis section */}
      {subCategoryData.length > 0 && (
        <div className="border-t pt-8">
          <div className="flex flex-col lg:flex-row lg:space-x-8">
            {/* Sub-category bar chart */}
            <div className="w-full lg:w-2/3 mb-6 lg:mb-0">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Expenses by Sub-Category
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={subCategoryData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={(value) => formatCurrency(value)}
                  />
                  <YAxis type="category" dataKey="name" width={70} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#10b981">
                    {subCategoryData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[(index + 5) % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Sub-category summary */}
            <div className="w-full lg:w-1/3">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                Sub-Category Summary
              </h3>

              {/* Grand total summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="text-sm text-gray-600 mb-1">
                  Total Sub-Category Expenses
                </div>
                <div className="text-xl font-bold text-gray-800">
                  {formatCurrency(totalSubCategorySpend)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {subCategoryPercentageOfTotal.toFixed(1)}% of total CC
                  expenses
                </div>
              </div>

              {/* Personal CC spending (excluding sub-categories) */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="text-sm text-gray-600 mb-1">
                  Personal CC Spending
                </div>
                <div className="text-xl font-bold text-blue-800">
                  {formatCurrency(totalSpent - totalSubCategorySpend)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  CC expenses without special categories
                </div>
              </div>

              {/* Individual sub-category breakdown */}
              <div className="space-y-2">
                {subCategoryData.map((item) => {
                  const percentage =
                    totalSubCategorySpend > 0
                      ? (item.value / totalSubCategorySpend) * 100
                      : 0;
                  const ccPercentage =
                    totalSpent > 0 ? (item.value / totalSpent) * 100 : 0;

                  return (
                    <div
                      key={item.name}
                      className="border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-gray-800">
                          {item.name}
                        </span>
                        <div className="text-right">
                          <div className="font-semibold text-gray-800">
                            {formatCurrency(item.value)}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>
                          {percentage.toFixed(1)}% of sub-category total
                        </div>
                        <div>{ccPercentage.toFixed(1)}% of all CC expenses</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border-t pt-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-1">
          Spending by Person
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Budgeted charges match a monthly budget category. Discretionary charges
          are everything else. Percent of budget compares each person&apos;s total
          to the monthly budget for this date range.
        </p>
        {personData.length > 0 ? (
          <>
            <div className="flex flex-col lg:flex-row">
              <div className="w-full lg:w-1/2 h-80 mb-6 lg:mb-0">
                <h3 className="text-md font-semibold text-gray-700 mb-4">
                  Share of spending
                </h3>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={personData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      labelLine={true}
                      label={renderPersonLabel}
                    >
                      {personData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={PERSON_COLORS[entry.name] || "#9ca3af"}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full lg:w-1/2">
                <h3 className="text-md font-semibold text-gray-700 mb-4">
                  Amount by person
                </h3>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={personData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <YAxis type="category" dataKey="name" width={90} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" name="Spent">
                      {personData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={PERSON_COLORS[entry.name] || "#9ca3af"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                Budgeted vs discretionary
              </h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={personData}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="budgeted" name="Budgeted" fill="#2563eb" />
                  <Bar
                    dataKey="discretionary"
                    name="Discretionary"
                    fill="#d97706"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-8 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">Person</th>
                    <th className="py-2 pr-4 text-right">Spent</th>
                    <th className="py-2 pr-4 text-right">% of spending</th>
                    <th className="py-2 pr-4 text-right">% of budget</th>
                    <th className="py-2 pr-4 text-right">Discretionary</th>
                    <th className="py-2 text-right">% of discretionary</th>
                  </tr>
                </thead>
                <tbody>
                  {personData.map((row) => (
                    <tr key={row.name} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium text-gray-800">
                        {row.name}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {formatCurrency(row.value)}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {row.percentOfSpending.toFixed(1)}%
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {personSpending.budgetTotal > 0
                          ? `${row.percentOfBudget.toFixed(1)}%`
                          : "—"}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {formatCurrency(row.discretionary)}
                      </td>
                      <td className="py-2 text-right">
                        {personSpending.totalDiscretionary > 0
                          ? `${row.percentOfDiscretionary.toFixed(1)}%`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-3">
                Monthly budget for this range:{" "}
                {formatCurrency(personSpending.budgetTotal)}. Discretionary
                spending: {formatCurrency(personSpending.totalDiscretionary)}.
              </p>
            </div>
          </>
        ) : (
          <div className="text-gray-500">
            No spending data available for the selected period
          </div>
        )}
      </div>

      {/* Historical Category Averages section */}
      <div className="border-t pt-8">
        <Category90DayAverage
          data={categoryHistoricalData}
          formatCurrency={formatCurrency}
          isLoading={averagesPending}
        />
      </div>
    </div>
  );
};

CategoryTab.propTypes = {
  spendByCategory: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      sub_category: PropTypes.string,
      responsible_person: PropTypes.string,
      final_category: PropTypes.string,
    }),
  ).isRequired,
  formatCurrency: PropTypes.func.isRequired,
  budgetComparison: PropTypes.array,
};

export default CategoryTab;
